import { ProductListEntityTagsFilterState } from "@/admin/community/product-list/ProductListTagsDropdown"
import EntityTag from "@/admin/settings/tabs/EntityTag"
import { EntityTagListPaginationFragment$key } from "@/admin/settings/tabs/__generated__/EntityTagListPaginationFragment.graphql"
import { EntityTagListPaginationQuery } from "@/admin/settings/tabs/__generated__/EntityTagListPaginationQuery.graphql"
import { EntityTagListQuery } from "@/admin/settings/tabs/__generated__/EntityTagListQuery.graphql"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import { GlobalID } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoSpinner, DiscoText } from "@disco-ui"
import DiscoScrolledIntoView from "@disco-ui/scrolled-into-view/DiscoScrolledIntoView"
import { ArrayUtils } from "@utils/array/arrayUtils"
import { TestIDProps } from "@utils/typeUtils"
import { Fragment, useMemo } from "react"
import { useLazyLoadQuery, usePaginationFragment } from "react-relay"
import { graphql } from "relay-runtime"

const TAG_USAGES_PER_LOAD = 20

interface Props extends TestIDProps {
  filterState: ProductListEntityTagsFilterState
  setFilterState: (state: ProductListEntityTagsFilterState) => void
  search: string
}

function EntityTagList({
  testid = "EntityTagList",
  filterState,
  setFilterState,
  search,
}: Props) {
  const activeOrganization = useActiveOrganization()!
  const classes = useStyles()

  const { organization } = useLazyLoadQuery<EntityTagListQuery>(
    graphql`
      query EntityTagListQuery(
        $id: ID!
        $first: Int!
        $after: String
        $search: String
        $ignoreIds: [ID!]
      ) {
        organization: node(id: $id) {
          ... on Organization {
            ...EntityTagListPaginationFragment
              @arguments(
                first: $first
                after: $after
                search: $search
                ignoreIds: $ignoreIds
              )
          }
        }
      }
    `,
    {
      id: activeOrganization.id,
      first: TAG_USAGES_PER_LOAD,
      search,
      ignoreIds: filterState.entityTags.map((tag) => tag.value),
    }
  )

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment<
    EntityTagListPaginationQuery,
    EntityTagListPaginationFragment$key
  >(
    graphql`
      fragment EntityTagListPaginationFragment on Organization
      @refetchable(queryName: "EntityTagListPaginationQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
        ignoreIds: { type: "[ID!]" }
      ) {
        entityTags(first: $first, after: $after, search: $search, ignoreIds: $ignoreIds)
          @connection(key: "EntityTagList__entityTags") {
          totalCount
          edges {
            node {
              id
              name
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    organization
  )

  const entityTags = Relay.connectionToArray(data?.entityTags)
  const selectedEntityTagsById = useMemo(
    () => ArrayUtils.mapBy(filterState.entityTags, "value"),
    [filterState.entityTags]
  )

  return (
    <div data-testid={testid} className={classes.list}>
      {entityTags.length > 0 ? (
        <>
          {entityTags.map((entityTag) => (
            <Fragment key={entityTag.id}>
              <EntityTag
                testid={testid}
                name={entityTag.name}
                isSelected={Boolean(selectedEntityTagsById?.[entityTag.id])}
                onClick={() => toggleEntityTag(entityTag.id, entityTag.name)}
              />
            </Fragment>
          ))}

          {hasNext && (
            <DiscoScrolledIntoView
              onScrolledIntoView={() => loadNext(TAG_USAGES_PER_LOAD)}
              isLoading={isLoadingNext}
              skeleton={<DiscoSpinner size={"xs"} />}
            />
          )}
        </>
      ) : (
        <DiscoText
          variant={"body-sm"}
          color={"text.secondary"}
          marginTop={2}
          align={"center"}
        >
          {"No tags found."}
        </DiscoText>
      )}
    </div>
  )

  function toggleEntityTag(id: GlobalID, name: string) {
    if (selectedEntityTagsById?.[id]) {
      const existingEntityTags = filterState.entityTags.filter((tag) => tag.value !== id)
      setFilterState({
        ...filterState,
        entityTags: existingEntityTags,
      })
    } else {
      setFilterState({
        ...filterState,
        entityTags: [...filterState.entityTags, { value: id, title: name }],
      })
    }
  }
}

const useStyles = makeUseStyles((theme) => ({
  list: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  skeleton: {
    marginTop: theme.spacing(2),
  },
}))

export function EntityTagListSkeleton() {
  const classes = useStyles()
  return <DiscoSpinner size={"xs"} containerClassName={classes.skeleton} />
}

export default Relay.withSkeleton({
  component: EntityTagList,
  skeleton: EntityTagListSkeleton,
})
